import React, { useState, useCallback } from 'react';
import './ResultsDisplay.css';
import { useApi } from "../api/useApi";
import { normalizeApiUrl } from "../api/apiClient";
import { getIncidentApiToken } from '../api/tokenService';

// Helper function to safely render any value
const capitalizeLabel = (key) =>
  String(key)
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (c) => c.toUpperCase());

const renderValue = (value) => {
  if (typeof value === 'string') {
    return value;
  } else if (typeof value === 'object' && value !== null) {
    if (Array.isArray(value)) {
      return value.join(', ');
    } else {
      return Object.entries(value)
        .map(([key, val]) => `${capitalizeLabel(key)}: ${val}`)
        .join('; ');
    }
  } else if (value === undefined || value === null) {
    return 'Not available';
  } else {
    return String(value);
  }
};

// EmailModal as a separate component to prevent flicker
const EmailModal = ({ show, emailAddresses, setEmailAddresses, onSubmit, onClose, sending }) => {
  if (!show) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-card" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Send via Email</h3>
          <button className="modal-close" onClick={onClose}>
            <svg viewBox="0 0 24 24" width="24" height="24">
              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
            </svg>
          </button>
        </div>
        <div className="modal-body">
          <label>Email addresses</label>
          <input
            type="text"
            value={emailAddresses}
            onChange={(e) => setEmailAddresses(e.target.value)}
            placeholder="e.g. user@example.com, colleague@company.com"
            autoFocus
          />
          <p className="input-hint">Separate multiple addresses with commas</p>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn btn-primary" onClick={onSubmit} disabled={sending}>
            {sending ? 'Sending...' : 'Send Report'}
          </button>
        </div>
      </div>
    </div>
  );
};
// Updated renderEvidenceDetails - handles FLAT structure directly
const renderEvidenceDetails = (item) => {
  if (!item) return null;

  return (
    <div className="evidence-details">
      {item.message && <div className="evidence-detail">📋 {item.message}</div>}
      {item.count && <div className="evidence-detail">📊 Count: {item.count}</div>}
      {item.firstSeen && <div className="evidence-detail">🕐 First: {new Date(item.firstSeen).toLocaleString()}</div>}
      {item.lastSeen && <div className="evidence-detail">🕐 Last: {new Date(item.lastSeen).toLocaleString()}</div>}
      {item.source && <div className="evidence-detail">📁 Source: {item.source}</div>}
      {item.name && item.value && <div className="evidence-detail">📈 {item.name}: {item.value}</div>}
      {item.threshold && <div className="evidence-detail">⚠️ Threshold: {item.threshold}</div>}
      {item.from && item.to && <div className="evidence-detail">🔗 {item.from} → {item.relationship || 'caused'} → {item.to}</div>}
      {item.timeDelta && <div className="evidence-detail">⏱️ Time delta: {item.timeDelta}ms</div>}
    </div>
  );
};

const TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'timeline', label: 'Timeline' },
  { id: 'evidence', label: 'Evidence' },
  { id: 'narrative', label: 'Narrative' },
  { id: 'resolution', label: 'Resolution' },
  { id: 'recommendations', label: 'Recommendations' },
];

const ResultsDisplay = ({ result, apiUrl }) => {
  const [exporting, setExporting] = useState(false);
  const [sending, setSending] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailAddresses, setEmailAddresses] = useState('');
  const [activeTab, setActiveTab] = useState('overview');
  const { post, postWithIdToken } = useApi();

  // Define callbacks before any conditional return
  const handleCloseModal = useCallback(() => {
    setShowEmailModal(false);
  }, []);

  const handleSubmitEmail = useCallback(async () => {
    if (!emailAddresses.trim()) {
      alert('Please enter at least one email address');
      return;
    }
    setSending(true);
    setShowEmailModal(false);
    try {
      const idToken = await getIncidentApiToken();
      const response = await postWithIdToken(`${apiUrl}/api/v1/notify/email`, {
        to: emailAddresses.split(',').map(e => e.trim()),
        analysisResult: result,
        includePdf: true,
      }, idToken);

      if (response.success) {
        alert('Email sent successfully!');
      } else {
        alert('Email sending failed: ' + response.data.error);
      }
    } catch (error) {
      console.error('Email error:', error);
      alert('Email sending failed. Please try again.');
    } finally {
      setSending(false);
      setEmailAddresses('');
    }
  }, [emailAddresses, apiUrl, postWithIdToken, result]);

  // Early return if no result (must be after hooks)
  if (!result) {
    return (
      <div className="no-result">
        <h3>No analysis result available</h3>
        <p>Submit incident data to see analysis results</p>
      </div>
    );
  }

  // --- Export PDF ---
  const handleExportPDF = async () => {
    setExporting(true);
    try {
      const idToken = await getIncidentApiToken();
      const response = await postWithIdToken(`${apiUrl}/api/v1/export/pdf`, {
        analysisResult: result,
        options: {}
      }, idToken);
      if (response.success) {
        const downloadResponse = await fetch(normalizeApiUrl(`${apiUrl}${response.data.downloadUrl}`), {
          headers: { Authorization: `Bearer ${idToken}` }
        });
        if (!downloadResponse.ok) throw new Error('PDF download failed');
        const blobUrl = URL.createObjectURL(await downloadResponse.blob());
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = response.data.fileName || 'incident-analysis.pdf';
        link.click();
        URL.revokeObjectURL(blobUrl);
      } else {
        alert('Export failed: ' + response.data.error);
      }
    } catch (error) {
      console.error('Export error:', error);
      alert('Export failed. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  // --- Send Email ---
  const handleSendEmail = () => {
    setShowEmailModal(true);
  };

  return (
    <div className="results-display">
      {/* Header with actions */}
      <div className="results-header">
        <div className="header-content">
          <h2>Analysis Results</h2>
          <div className="header-actions">
            <button
              onClick={handleExportPDF}
              disabled={exporting}
              className="btn btn-export"
            >
              {exporting ? 'Exporting...' : '📄 Export PDF'}
            </button>
            <button
              onClick={handleSendEmail}
              disabled={sending}
              className="btn btn-email"
            >
              {sending ? 'Sending...' : '✉️ Send Email'}
            </button>
          </div>
        </div>
        <div className="result-meta">
          <span className="incident-id">ID: {result.id || 'Unknown'}</span>
          <span className="processing-time">
            <span>⏱️</span> {result.metadata?.processingTime || 'Unknown'}
          </span>
          {result.metadata?.hasRealAI && (
            <span className="ai-badge">AI Analysis</span>
          )}
        </div>
      </div>

      {/* Tabbed results */}
      <div className="results-tabs">
        <div className="tab-bar" role="tablist">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={activeTab === tab.id}
              className={`tab-btn ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="tab-content">
          {activeTab === 'overview' && (
            <>
              {/* Summary */}
              <div className="result-section">
                <h3>Summary</h3>
                <div className="summary-box">
                  <p>{renderValue(result.summary)}</p>
                </div>
              </div>

              {/* Root Cause & Impact */}
              <div className="info-grid">
                <div className="result-section">
                  <h3>Root Cause</h3>
                  <div className="info-box">
                    <p>{renderValue(result.rootCause)}</p>
                  </div>
                </div>
                <div className="result-section">
                  <h3>Impact</h3>
                  <div className="info-box">
                    <p>{renderValue(result.impact)}</p>
                  </div>
                </div>
              </div>
            </>
          )}

          {activeTab === 'timeline' && (
            <div className="result-section">
              <h3>Timeline</h3>
              <div className="timeline">
                {result.timeline && Array.isArray(result.timeline) ? (
                  result.timeline.map((event, index) => (
                    <div key={index} className="timeline-event">
                      <div className="timeline-time">
                        {event.timestamp ? new Date(event.timestamp).toLocaleString() : 'Unknown time'}
                      </div>
                      <div className="timeline-content">
                        {renderValue(event.event)}
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="timeline-event">
                    <div className="timeline-content">No timeline data available</div>
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'evidence' && (
            <div className="result-section">
              <h3>🔍 Evidence & Supporting Data</h3>
              {result.evidence && Array.isArray(result.evidence) && result.evidence.length > 0 ? (
                <div className="evidence-list">
                  {result.evidence.map((item, index) => (
                    <div key={index} className={`evidence-item evidence-${item.type}`}>
                      <div className="evidence-header">
                        <span className="evidence-icon">
                          {item.type === 'log' && '📝'}
                          {item.type === 'metric' && '📊'}
                          {item.type === 'pattern' && '🔄'}
                          {item.type === 'causal_link' && '🔗'}
                          {item.type === 'trace' && '🔍'}
                        </span>
                        <span className="evidence-type">{item.type?.toUpperCase() || 'EVIDENCE'}</span>
                      </div>

                      <div className="evidence-description">
                        {item.description || item.message || `${item.type} evidence found`}
                      </div>

                      {renderEvidenceDetails(item)}

                      {item.confidence !== undefined && (
                        <div className="evidence-confidence">
                          Confidence: {(item.confidence * 100).toFixed(1)}%
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="info-box">
                  <p>No evidence available</p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'narrative' && (
            <div className="result-section">
              <h3>Detailed Narrative</h3>
              <div className="narrative-box">
                <p>{renderValue(result.narrative)}</p>
              </div>
            </div>
          )}

          {activeTab === 'resolution' && (
            <div className="result-section">
              <h3>Resolution</h3>
              <div className="info-box">
                <p>{renderValue(result.resolution)}</p>
              </div>
            </div>
          )}

          {activeTab === 'recommendations' && (
            <div className="result-section">
              <h3>Recommendations</h3>
              <div className="recommendations">
                {result.recommendations && Array.isArray(result.recommendations) ? (
                  result.recommendations.map((rec, index) => {
                    let recommendationText = '';
                    let priority = '';

                    if (typeof rec === 'string') {
                      recommendationText = rec;
                    } else if (rec && typeof rec === 'object') {
                      recommendationText =
                        rec.action || rec.description || rec.text || rec.recommendation;
                      if (!recommendationText) {
                        recommendationText = JSON.stringify(rec, null, 2).replace(/[{}"]/g, '');
                      }
                      priority = String(rec.priority || '').toLowerCase();
                    }

                    const priorityClass =
                      priority === 'high' || priority === 'critical'
                        ? 'priority-high'
                        : priority === 'medium'
                          ? 'priority-medium'
                          : priority === 'low'
                            ? 'priority-low'
                            : '';

                    return (
                      <div key={index} className={`recommendation-item ${priorityClass}`.trim()}>
                        <span className="rec-number">{index + 1}</span>
                        <span className="rec-text">
                          {priority && (
                            <span className={`priority-tag ${priorityClass}`.trim()}>
                              {priority.toUpperCase()}
                            </span>
                          )}
                          {recommendationText || 'No description'}
                        </span>
                      </div>
                    );
                  })
                ) : (
                  <div className="recommendation-item">
                    <span className="rec-number">1</span>
                    <span className="rec-text">No recommendations available</span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Email modal */}
      <EmailModal
        show={showEmailModal}
        emailAddresses={emailAddresses}
        setEmailAddresses={setEmailAddresses}
        onSubmit={handleSubmitEmail}
        onClose={handleCloseModal}
        sending={sending}
      />
    </div>
  );
};

export default ResultsDisplay;